import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMemoizedFn } from 'ahooks'
import { useCommon } from '@/core/useCommon'
import { clearLoading, setLoading, toast } from '@/ui/useToastStore'
import { alerts } from '@/ui/useAlertStore'
import { useUserInfo } from '@/store/user/useUserStore'
import { reqApiAgentUserUpdatepwd } from '@/apis/ok'
import { md5 } from '@/utils'
import { isStrongPassword } from './passwordRules'

type FormValues = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

type Props = {
  // 提交前是否顯示確認彈窗
  confirm?: boolean
  // 修改成功
  onSuccess?: () => void
}

// 舊密碼錯誤的錯誤碼
const OLD_PASSWORD_INCORRECT_CODE = 1534

const defaultValues: FormValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
}

// 登入密碼修改 hook (密碼強度規則)
export const useStrongPasswordForm = ({ confirm, onSuccess }: Props = {}) => {
  const { t } = useCommon()
  const { userInfo } = useUserInfo()
  // 重置時重新掛載輸入框，清除明文/聚焦等狀態
  const [resetKey, setResetKey] = useState(0)

  const form = useForm<FormValues>({ defaultValues })
  const { oldPassword, newPassword, confirmPassword } = form.watch()

  // 新密碼是否滿足全部規則
  const isNewPasswordStrong = isStrongPassword(newPassword)
  // 確認密碼比對: 為空時不提示
  const matchStatus = !confirmPassword ? 'empty' : confirmPassword === newPassword ? 'match' : 'mismatch'
  // 提交按鈕解鎖條件
  const isDisabled = !oldPassword || !isNewPasswordStrong || matchStatus !== 'match'

  // 重置表單
  const onReset = useMemoizedFn(() => {
    ;(document.activeElement as HTMLElement | null)?.blur?.()
    form.reset(defaultValues)
    setResetKey((key) => key + 1)
  })

  // 修改失敗: 舊密碼錯誤顯示前端訊息並清空三個輸入框，其餘顯示後端訊息
  const onFail = (res?: { code?: number | string; msg?: string }) => {
    if (Number(res?.code) === OLD_PASSWORD_INCORRECT_CODE) {
      toast({ message: t('old:pwdOldIncorrect') })
      onReset()
      return
    }
    toast({ message: res?.msg || t('old:failure') })
  }

  // 更新密碼
  const updatePassword = async (data: FormValues) => {
    try {
      setLoading()
      const result = await reqApiAgentUserUpdatepwd({
        userId: userInfo?.userId,
        platformId: userInfo?.platformId,
        oldPassword: md5(data.oldPassword),
        newPassword: md5(data.newPassword),
      })

      if (!result.success) return onFail(result)

      onSuccess?.()
    } catch (error: any) {
      // 後端錯誤回 HTTP 400，內容在 response.data
      onFail(error?.response?.data)
    } finally {
      clearLoading()
    }
  }

  const onSubmit = form.handleSubmit(async (data) => {
    if (isDisabled) return

    if (!confirm) return updatePassword(data)

    // 顯示確認彈窗
    alerts({
      title: t('A013'),
      message: t('old:global.confirmPrompt'),
      confirm: t('old:confirmButtonText'),
      close: t('old:cancelButtonText'),
      classNames: {
        content: 'w-[90%] sm:w-[420px]',
        footer: 'w-full flex',
      },
      closeProps: {
        className: 'flex-1',
      },
      confirmProps: {
        className: 'flex-1',
      },
      onConfirm: async ({ close }) => {
        close?.()
        await updatePassword(data)
      },
    })
  })

  return {
    form, // 表單
    resetKey, // 重置 key
    oldPassword, // 舊密碼
    newPassword, // 新密碼
    confirmPassword, // 確認密碼
    isNewPasswordStrong, // 新密碼是否滿足規則
    matchStatus, // 確認密碼比對狀態
    isDisabled, // 提交按鈕是否禁用
    onSubmit, // 提交
    onReset, // 重置
  }
}
